import axios from 'axios';
import { RateLimiter } from './rateLimiter';

const apiKey = process.env.REACT_APP_LASTFM_API_KEY;
const ROOT = "https://ws.audioscrobbler.com/2.0";

const rateLimiter = new RateLimiter({
    requestsPerSecond: 5,
    maxConcurrent: 3,
    retryAttempts: 3,
    baseDelay: 1000,
    maxDelay: 30000,
    shouldRetry: (error) => {
        if (!error.response) return true;
        const status = error.response.status;
        return status === 429 || status === 503 || status === 502 || status >= 500;
    }
});

const apiClient = axios.create({
    timeout: 10000,
    headers: {
        'Content-Type': 'application/json',
    }
});

apiClient.interceptors.request.use(
    (config) => {
        console.debug(`API Request: ${config.method?.toUpperCase()} ${config.url}`);
        return config;
    },
    (error) => Promise.reject(error)
);

apiClient.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.code === 'ECONNABORTED') {
            console.error('Request timeout:', error.config?.url);
        } else if (error.response) {
            console.error(`API Error ${error.response.status}:`, error.response.data);
        } else if (error.request) {
            console.error('Network error:', error.message);
        }
        return Promise.reject(error);
    }
);

export const getSimilarMusicians = async (artist, limit = 6, autocorrect = true) => {
    if (!artist || typeof artist !== 'string' || artist.trim() === '') {
        console.warn('Invalid artist name provided to getSimilarMusicians');
        return [];
    }

    const requestFn = async () => {
        const callURL = `${ROOT}/?method=artist.getsimilar&artist=${encodeURIComponent(artist)}&limit=${limit}&autocorrect=${autocorrect}&api_key=${apiKey}&format=json`;

        const response = await apiClient.get(callURL);

        if (!response.data?.similarartists?.artist) {
            console.warn(`No similar artists found for: ${artist}`);
            return [];
        }

        const artists = response.data.similarartists.artist;
        return Array.isArray(artists) ? artists : [artists];
    };

    try {
        return await rateLimiter.makeRequest(requestFn);
    } catch (error) {
        console.error(`Error fetching similar musicians for "${artist}":`, error.message);
        return [];
    }
};

const TAG_FILTER = ["seen live"];

const normalizeTag = (tag) => {
    return tag
        .toLowerCase()
        .replace(/\s+/g, '-');
};

export const getTopTags = async (artist, limit = 5, filter = true) => {
    if (!artist || typeof artist !== 'string' || artist.trim() === '') {
        console.warn('Invalid artist name provided to getTopTags');
        return [];
    }

    const requestFn = async () => {
        const callURL = `${ROOT}/?method=artist.gettoptags&artist=${encodeURIComponent(artist)}&api_key=${apiKey}&format=json`;

        const response = await apiClient.get(callURL);

        if (!response.data?.toptags?.tag) {
            console.warn(`No tags found for: ${artist}`);
            return [];
        }

        const tags = response.data.toptags.tag;
        const tagArray = Array.isArray(tags) ? tags : [tags];

        const filteredTags = filter
            ? tagArray.filter(tag => !TAG_FILTER.includes(tag.name?.toLowerCase()))
            : tagArray;

        const uniqueTags = new Map();

        filteredTags.forEach(tag => {
            if (!tag?.name) return;

            const normalizedName = normalizeTag(tag.name);
            if (!uniqueTags.has(normalizedName) ||
                (tag.count && tag.count > uniqueTags.get(normalizedName).count)) {
                uniqueTags.set(normalizedName, tag);
            }
        });

        return Array.from(uniqueTags.values())
            .sort((a, b) => (b.count || 0) - (a.count || 0))
            .slice(0, limit)
            .map(tag => normalizeTag(tag.name));
    };

    try {
        return await rateLimiter.makeRequest(requestFn);
    } catch (error) {
        console.error(`Error fetching tags for "${artist}":`, error.message);
        return [];
    }
};

export const getTopMusiciansFromUsername = async (username, top = 5, period = 'overall') => {
    if (!username || typeof username !== 'string' || username.trim() === '') {
        console.warn('Invalid username provided to getTopMusiciansFromUsername');
        return [];
    }

    const validPeriods = ["overall", "7day", "1month", "3month", "6month", "12month"];
    if (!validPeriods.includes(period)) {
        console.warn(`Invalid period "${period}". Using "overall" instead.`);
        period = 'overall';
    }

    const requestFn = async () => {
        const callURL = `${ROOT}/?method=user.gettopartists&user=${encodeURIComponent(username)}&api_key=${apiKey}&format=json&period=${period}&limit=${top}`;

        const response = await apiClient.get(callURL);

        if (!response.data?.topartists?.artist) {
            console.warn(`No artists found for user: ${username}`);
            return [];
        }

        const artists = response.data.topartists.artist;
        const artistArray = Array.isArray(artists) ? artists : [artists];

        return artistArray
            .filter(artist => artist?.name)
            .map(artist => artist.name);
    };

    try {
        return await rateLimiter.makeRequest(requestFn);
    } catch (error) {
        console.error(`Error fetching top musicians for user "${username}":`, error.message);
        return [];
    }
};

export const getRateLimiterStats = () => rateLimiter.getStats();
